/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import Editor, { Monaco } from '@monaco-editor/react';
import { MIMO_THEME, MONACO_BASE_OPTIONS } from '@utils/code-editor-theme';
import { ExecutableLessonFile } from '@customtypes/parsedLesson';
import { CodeEditorTabBar } from './CodeEditorTabBar';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { getCodeLanguageForEditor } from '@utils/content-utils';
import { Tooltip } from '@utils/tooltip-utils';
import CodeIconComponent from '@components/icons/codeIcon';
import { regular_xs } from '@components/web/texts/Styles';
import EyeOpenIconComponent from '@components/icons/eyeOpenIcon';
import CloseIconComponent from '@components/icons/closeIcon';
import PrimaryButtonComponent from '@components/buttons/PrimaryButton';
import { Button2Text } from '@components/web/texts/Texts';
import { ConfirmApplySolutionModal } from './ConfirmApplySolutionModal';
import { useRouter } from 'next/router';
import {
  APPLY_SOLUTION_MODAL_QUERY_PARAM,
  TOOLTIP_DELAY_SHOW,
} from '@utils/constants';
import { twMerge } from 'tailwind-merge';

function CodeCompareFullscreenComponent({
  files,
  onClose,
  initialSelectedFile,
  applySolutionCode,
  className,
}: {
  files: ExecutableLessonFile[];
  onClose: () => void;
  initialSelectedFile: number;
  applySolutionCode?: () => void;
  className?: string;
}) {
  const router = useRouter();
  const [selectedFileIndex, setSelectedFileIndex] =
    useState(initialSelectedFile);

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);

  function handleApplySolutionClick() {
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, [APPLY_SOLUTION_MODAL_QUERY_PARAM]: true },
      },
      undefined,
      { shallow: true },
    );
  }

  return (
    <div
      className={`${twMerge('absolute inset-0 z-50 flex items-center justify-center p-6', className)} bg-product2-background-dark/80`}
    >
      {applySolutionCode && router.query[APPLY_SOLUTION_MODAL_QUERY_PARAM] && (
        <ConfirmApplySolutionModal confirm={applySolutionCode} />
      )}
      <div
        onClick={onClose}
        role="region"
        className="absolute inset-0 z-20"
      ></div>
      <div className="relative z-20 flex h-full max-h-[1000px] w-full max-w-[1800px] flex-col overflow-hidden rounded-xl border-2 border-product2-border-primary bg-product-background-primary-dark shadow-md">
        <div className="flex divide-x divide-product2-border-secondary bg-product2-background-secondary">
          <div className="flex flex-1 items-center space-x-2 p-3">
            <CodeIconComponent className="h-4 w-4" />
            <p className={`${regular_xs}`}>Your Code</p>
          </div>
          <div className="flex flex-1 items-center space-x-2 p-3">
            <EyeOpenIconComponent className="h-4 w-4" />
            <p className={`${regular_xs}`}>Our solution</p>
          </div>
        </div>
        <div className="grid h-full flex-grow grid-cols-2 divide-x divide-product2-border-secondary">
          <div className="relative flex flex-col bg-product-background-secondary-dark">
            <CompareCodeEditorComponent
              files={files}
              selectedFileIndex={selectedFileIndex}
              setSelectedFileIndex={setSelectedFileIndex}
              readOnly={true}
              id={'editor1'}
              isPrimary={true}
            ></CompareCodeEditorComponent>
          </div>
          <div className="relative flex flex-col bg-product-background-primary-dark">
            <CompareCodeEditorComponent
              files={
                files.map((f) => {
                  return { ...f, content: f.solvedContent };
                }) as any
              }
              selectedFileIndex={selectedFileIndex}
              setSelectedFileIndex={setSelectedFileIndex}
              readOnly={true}
              id={'editor2'}
              isPrimary={false}
            ></CompareCodeEditorComponent>
          </div>
        </div>
        <ReactTooltip
          className="defaultTooltip"
          id="runButton1"
          place="top"
          opacity={1}
          noArrow
          delayShow={TOOLTIP_DELAY_SHOW}
        ></ReactTooltip>
        <button
          data-tooltip-id="runButton1"
          data-tooltip-html={Tooltip(
            `<span>Press</span><span class="border border-product-line-secondary-dark rounded p-1">Esc</span>`,
          )}
          onClick={onClose}
          className="absolute right-1.5 top-1.5"
        >
          <div className="rounded-lg p-1.5 hover:bg-product2-background-tertiary">
            <CloseIconComponent className="z-20 h-4 w-4 flex-shrink-0 text-product2-content-secondary " />
          </div>
        </button>
        {applySolutionCode && (
          <PrimaryButtonComponent
            className="absolute bottom-4 right-4 flex items-center justify-center space-x-2 rounded-lg px-4 py-1.5"
            onClick={handleApplySolutionClick}
          >
            <Button2Text>Use solution</Button2Text>
          </PrimaryButtonComponent>
        )}
      </div>
    </div>
  );
}

export default CodeCompareFullscreenComponent;

interface CodeEditorProps {
  files: ExecutableLessonFile[];
  selectedFileIndex: number;
  setSelectedFileIndex: (fileIndex: number) => void;
  readOnly: boolean;
  id: string;
  isPrimary: boolean;
}

const CompareCodeEditorComponent = ({
  files,
  selectedFileIndex,
  setSelectedFileIndex,
  readOnly = false,
  id,
  isPrimary,
}: CodeEditorProps) => {
  function handleEditorWillMount(monaco: Monaco) {
    monaco.editor.defineTheme('mimo', MIMO_THEME);
  }

  return (
    <div
      className="monaco-editor-container relative flex h-full flex-col"
      translate="no"
    >
      <div className="flex h-12">
        <CodeEditorTabBar
          files={files}
          selectedFileIndex={selectedFileIndex}
          setSelectedFileIndex={setSelectedFileIndex}
        ></CodeEditorTabBar>
      </div>
      <Editor
        className="absolute inset-0"
        wrapperProps={{ className: 'relative' }}
        theme={'mimo'}
        path={files[selectedFileIndex].name + id}
        language={getCodeLanguageForEditor(
          files[selectedFileIndex].codeLanguage,
        )}
        value={files[selectedFileIndex].content}
        beforeMount={handleEditorWillMount}
        options={{
          extraEditorClassName: 'myeditor',
          ...MONACO_BASE_OPTIONS,
          wordWrap: 'wordWrapColumn',
          wordWrapColumn: 80,
          padding: { top: 20, bottom: 70 },
          readOnly,
          renderLineHighlight: readOnly ? 'none' : 'line',
          suggest: {
            snippetsPreventQuickSuggestions: false,
          },
          minimap: {
            enabled: false,
          },
          overviewRulerBorder: false,
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          colorDecorators: true,
        }}
      />
    </div>
  );
};
